<template>
    <div
        class="banner-button-v2"
        :class="'count-' + renderButtons.length"
        v-if="renderButtons?.length > 0"
        :style="rootStyle"
    >
        <a
            v-for="(button, index) in renderButtons"
            :key="button.link + '-' + index"
            :href="['callback', 'anchor'].includes(button.type) ? null : button.link"
            class="btn-item"
            :class="{ plain: button.plain }"
            rel="noopener noreferrer"
            :target="button.target || '_blank'"
            :aria-label="getButtonLabel(button)"
            @click="click(button, $event)"
            :style="getStyle(button)"
            :ref="el => setRefs(el, index)"
        >
            <span
                class="button-text"
                v-if="showText"
                v-html="getButtonLabel(button)"
            ></span>
            <i :class="['iconfont', arrowIcon]" v-show="showArrow"></i>
        </a>
    </div>
</template>

<script>
import util from '../common/util.js';
import { getButtonHtmlStyle } from '@/common/utils/decode-html';
export default {
    emits: ['callback', 'scrollTo'],
    props: {
        size: {
            type: Number,
            default: 0
        },
        buttons: {
            type: Array,
            default: () => []
        },
        showArrow: {
            type: Boolean,
            default: false
        },
        showText: {
            type: Boolean,
            default: true
        },
        arrowIcon: {
            type: String,
            default: 'icon-arrow-up'
        },
        fitWidth: {
            type: Boolean,
            default: false
        }
    },
    data() {
        return {
            maxButtonWidth: null, // 存储计算后的最大宽度
            calculated: false,
            buttonRefs: [],
            borderOffsetWidth: 4 // 4px is for border
        };
    },
    watch: {
        renderButtons() {
            this.maxButtonWidth = null;
            this.buttonRefs = []; // 清空旧的 refs，避免残留
            this.$nextTick(() => {
                this.calculateButtonsWidths();
            });
        }
    },
    computed: {
        renderButtons() {
            const buttons = [];
            this.buttons.forEach(item => {
                const text = this.getButtonLabel(item);
                if (text || (!this.showText && this.showArrow)) {
                    buttons.push(item);
                }
            });
            return buttons;
        },
        rootStyle() {
            if (this.fitWidth === true) {
                return {
                    '--sync-button-width': this.maxButtonWidth
                };
            }
            return {};
        }
    },
    methods: {
        setRefs(ref, index) {
            this.buttonRefs[index] = ref;
        },
        calculateButtonsWidths() {
            if (!this.fitWidth || util.isSSRServer) {
                this.calculated = true;
                return;
            }
            this.calculated = false;
            this.maxButtonWidth = `${
                this.buttonRefs.reduce((prev, curr) => {
                    const currentWidth = curr?.scrollWidth ?? 0;
                    return Math.max(prev, currentWidth);
                }, 0) + this.borderOffsetWidth
            }px`;
            this.calculated = true;
        },
        getButtonLabel(button) {
            let text =
                button.label ||
                button.text ||
                (button.textOption &&
                    (button.textOption?.text || button.textOption?.html)) ||
                '';
            return text.replace(/<[^>]*>/g, '');
        },
        click(button, event) {
            // If there is type field and not 'url' or 'anchor',
            // when type is callback, click it to pop up the registration box
            // otherwise to scroll to target element.
            switch (button?.type) {
            case 'callback':
                event.preventDefault();
                this.$emit('callback', button);
                break;
            case 'anchor':
                event.preventDefault();
                this.$emit('scrollTo', button.anchor);
                break;
            case 'url':
            default:
                // use a tag default behavior
                // a tag has its href attribute, so it will be handled by the browser
                break;
            }
        },
        getStyle(button) {
            let style = '';
            switch (button.plain) {
            case true:
                style = {
                    color: button.color,
                    borderColor: button.color,
                    backgroundColor: 'transparent'
                };
                break;
            case false:
                style = {
                    color: button.textColor,
                    backgroundColor: button.color,
                    borderColor: button.color
                };
                break;
            default:
                style = {
                    color: button.textColor,
                    backgroundColor: button.color,
                    borderColor: button.borderColor || button.color
                };
                break;
            }
            if (this.showText) {
                style = { ...style, ...getButtonHtmlStyle(button) };
            }
            return style;
        }
    },
    mounted() {
        if (this.fitWidth) {
            this.$watch(
                'renderButtons',
                () => {
                    this.calculateButtonsWidths();
                },
                { immediate: true }
            );
        }
    }
};
</script>

<style lang="scss">
@import '../theme/media.scss';
@import '../theme/mixin.scss';
.banner-button-v2 {
    width: 100%;
    display: flex;
    justify-content: center;
    flex-wrap: wrap;
    .btn-item {
        position: relative;
        margin: 10px 5px 0 5px;
        padding: 0 20px;
        min-width: 190px;
        overflow: hidden;
        height: 40px;
        line-height: 38px;
        display: block;
        text-shadow: 0 2px 12px rgba(0, 0, 0, 0.1);
        font-size: 12px;
        font-weight: 500;
        font-family: var(--mg-button-font);
        text-align: center;
        border: 1px solid;
        text-overflow: ellipsis;
        white-space: nowrap;
        transition: width 0.3s ease;
        cursor: pointer;
        @include color-button-v2();
        @include color-button-hover();
        .icon-arrow-up {
            transform: rotate(90deg);
            display: inline-block;
            font-size: 14px;
        }
        &.plain {
            border-color: var(--g-btn-background, #fff);
            color: var(--g-btn-background, #fff);
        }
    }
}
</style>
